import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API_BASE } from '../config/api';
import { 
  Clock, 
  Award, 
  Users, 
  Star, 
  Play, 
  BookOpen,
  CheckCircle,
  ChevronRight,
  ExternalLink,
  ShoppingCart
} from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { Breadcrumb } from '../components/ui/Breadcrumb';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { formatPrice, formatDurationHours, getThumbnailUrl } from '../lib/utils';
import { toast } from 'sonner';
import EnrollmentModal from '../components/EnrollmentModal';
const API = `${API_BASE}/api`;

export default function CourseDetailPage() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { user, token, isAuthenticated } = useAuth();
  const { tenant } = useTenant();
  
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [enrollment, setEnrollment] = useState(null);
  const [showEnrollmentModal, setShowEnrollmentModal] = useState(false);

  // Feature flags for enterprise tenants
  const isEnterprise = tenant.features?.subscription === false;
  const showCourseContent = tenant.features?.course_content !== false; // New flag, defaults to true

  useEffect(() => {
    const fetchCourse = async () => {
      try {
        let res;
        if (token) {
          // Authenticated user - use authenticated endpoint (supports both course_id and slug)
          res = await axios.get(`${API}/courses/${courseId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
        } else {
          // Public user - use public endpoint (supports both course_id and slug)
          res = await axios.get(`${API}/public/courses/${courseId}`);
        }
        setCourse(res.data);

        // Check if user is enrolled - need to check by actual course_id from response
        if (token && res.data?.course_id) {
          try {
            const enrollRes = await axios.get(`${API}/enrollments`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            // Use the actual course_id from the response, not the URL param (which could be slug)
            const userEnrollment = enrollRes.data.find(e => e.course_id === res.data.course_id);
            setEnrollment(userEnrollment || null);
          } catch (err) {
            console.error('Error checking enrollment:', err);
          }
        }
      } catch (error) {
        console.error('Error fetching course:', error);
        toast.error('Failed to load course');
      } finally {
        setLoading(false);
      }
    };

    fetchCourse();
  }, [courseId, token]);

  const handleEnroll = async () => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: { pathname: `/courses/${courseId}` } } });
      return;
    }

    if (course.price > 0) {
      // Show enrollment modal for paid courses
      setShowEnrollmentModal(true);
      return;
    }

    // Free course - direct enrollment
    setEnrolling(true);
    try {
      // Use actual course_id from fetched course, not the URL param (which could be a slug)
      await axios.post(`${API}/enrollments`, 
        { course_id: course.course_id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Successfully enrolled!');
      
      // Check if this course has a Moodle URL - redirect to Moodle
      if (course.moodle_course_url) {
        toast.info('Redirecting to Moodle to complete this course...', { duration: 3000 });
        setTimeout(() => {
          window.open(course.moodle_course_url, '_blank');
        }, 1500);
        return;
      }
      
      // Route to AI player for AI-generated courses, otherwise regular player
      if (course.ai_generated || course.ai_course_data) {
        navigate(`/courses/${courseId}/ai-learn`);
      } else {
        navigate(`/courses/${courseId}/learn`);
      }
    } catch (error) {
      console.error('Enrollment error:', error);
      // Check if already enrolled
      if (error.response?.status === 400 && error.response?.data?.detail?.includes('Already enrolled')) {
        toast.error('You are already enrolled on this course. To complete, visit your dashboard and click on enrolled courses.', {
          duration: 6000,
          action: {
            label: 'Go to Dashboard',
            onClick: () => navigate('/dashboard')
          }
        });
      } else {
        toast.error(error.response?.data?.detail || 'Failed to enroll');
      }
    } finally {
      setEnrolling(false);
    }
  };

  const handleEnrollmentSuccess = () => {
    setShowEnrollmentModal(false);
    // Refresh enrollment status
    
    // Check if this course has a Moodle URL - redirect to Moodle
    if (course.moodle_course_url) {
      toast.info('Redirecting to Moodle to complete this course...', { duration: 3000 });
      setTimeout(() => {
        window.open(course.moodle_course_url, '_blank');
      }, 1500);
      return;
    }
    
    if (course.ai_generated || course.ai_course_data) {
      navigate(`/courses/${courseId}/ai-learn`);
    } else {
      navigate(`/courses/${courseId}/learn`);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse">
            <div className="h-8 bg-zinc-800 rounded w-3/4 mb-4" />
            <div className="h-4 bg-zinc-800 rounded w-1/2 mb-8" />
            <div className="grid lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 space-y-4">
                <div className="aspect-video bg-zinc-800 rounded-xl" />
                <div className="h-24 bg-zinc-800 rounded-xl" />
              </div>
              <div className="h-96 bg-zinc-800 rounded-xl" />
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!course) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <BookOpen className="h-16 w-16 text-zinc-600 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Course Not Found</h1>
          <p className="text-zinc-400 mb-6">The course you&apos;re looking for doesn&apos;t exist.</p>
          <Link to="/courses" className="btn-gold">
            Browse Courses
          </Link>
        </div>
      </Layout>
    );
  }

  const defaultThumbnail = "https://images.unsplash.com/photo-1705234384669-c6d31c61b789?crop=entropy&cs=srgb&fm=jpg";
  
  // Calculate modules and lessons - handle both modules and topics structures
  const getModuleCount = () => {
    if (course.modules?.length > 0) return course.modules.length;
    if (course.topics?.length > 0) return course.topics.length;
    // For AICC/external content with direct content_type, treat as 1 module
    if (course.content_type === 'aicc' || course.content_type === 'external' || course.content_type === 'moodle') return 1;
    return 0;
  };
  
  const getTotalLessons = () => {
    // Standard modules structure
    if (course.modules?.length > 0) {
      return course.modules.reduce((acc, m) => acc + (m.contents?.length || m.lessons?.length || 0), 0);
    }
    // Topics structure (used by AICC courses)
    if (course.topics?.length > 0) {
      return course.topics.reduce((acc, t) => acc + (t.modules?.length || 0), 0);
    }
    // For AICC/external content, count as 1 lesson
    if (course.content_type === 'aicc' || course.content_type === 'external' || course.content_type === 'moodle') return 1;
    return 0;
  };
  
  const moduleCount = getModuleCount();
  const totalLessons = getTotalLessons();

  return (
    <Layout showBreadcrumb={false}>
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
        <Breadcrumb 
          items={[
            { label: 'Dashboard', href: '/dashboard', isRoot: true },
            { label: 'Courses', href: '/courses' },
            { label: course.title, isCurrent: true }
          ]} 
        />
      </div>
      
      {/* Hero Section */}
      <div className="bg-navigate-surface border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Course Info */}
            <div className="lg:col-span-2">
              <div className="flex items-center gap-2 mb-4">
                <span className="tag">{course.category}</span>
                <span className="text-xs text-zinc-500 capitalize">{course.level}</span>
              </div>
              
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
                {course.title}
              </h1>
              
              <p className="text-zinc-400 mb-6 text-lg">
                {course.description}
              </p>

              {/* Meta Info */}
              <div className="flex flex-wrap gap-6 mb-6">
                <div className="flex items-center gap-2 text-zinc-400">
                  <Clock className="h-5 w-5 text-navigate-gold" />
                  <span>{formatDurationHours(course.duration_hours) || '30 mins'}</span>
                </div>
                <div className="flex items-center gap-2 text-zinc-400">
                  <BookOpen className="h-5 w-5 text-navigate-gold" />
                  <span>{moduleCount} {moduleCount === 1 ? 'Module' : 'Modules'}</span>
                </div>
              </div>

              {course.instructor_name && (
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-navigate-gold/20 flex items-center justify-center text-navigate-gold font-bold">
                    {course.instructor_name.charAt(0)}
                  </div>
                  <div>
                    <p className="text-white font-medium">{course.instructor_name}</p>
                    <p className="text-sm text-zinc-500">Instructor</p>
                  </div>
                </div>
              )}
            </div>

            {/* Enrollment Card */}
            <div className="lg:row-start-1">
              <div className="bg-navigate-dark border border-zinc-800 rounded-xl overflow-hidden sticky top-24">
                <div className="aspect-video relative">
                  <img 
                    src={getThumbnailUrl(course.thumbnail_url)}
                    alt={course.title}
                    className="w-full h-full object-cover"
                  />
                </div>

                <div className="p-6">
                  {/* Price display - Hide "Free" for enterprise tenants */}
                  {!isEnterprise && (
                    <div className="flex items-center justify-between mb-6">
                      <span className="price-tag text-3xl">
                        {course.price > 0 ? formatPrice(course.price, course.currency) : 'Free'}
                      </span>
                    </div>
                  )}
                  {/* Enterprise mode - no CPD badge */}

                  {enrollment ? (
                    <div className="space-y-4">
                      <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                        <div className="flex items-center gap-2 text-green-500 mb-2">
                          <CheckCircle className="h-5 w-5" />
                          <span className="font-medium">Enrolled</span>
                        </div>
                        <p className="text-sm text-zinc-400">
                          Progress: {enrollment.progress_percent}%
                        </p>
                        {enrollment.moodle_completed_at && (
                          <p className="text-sm text-green-400 mt-1">
                            ✓ Completed in Moodle
                          </p>
                        )}
                      </div>
                      {course.moodle_course_url ? (
                        <>
                          <a 
                            href={course.moodle_course_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-gold w-full flex items-center justify-center gap-2"
                          >
                            <ExternalLink className="h-5 w-5" />
                            {enrollment.moodle_completed_at ? 'Review in Moodle' : 'Launch Course in Moodle'}
                          </a>
                          <p className="text-xs text-zinc-500 text-center">
                            This course is hosted on our Moodle platform. Click above to continue learning.
                          </p>
                        </>
                      ) : (
                        <Link 
                          to={course.ai_generated || course.ai_course_data 
                            ? `/courses/${courseId}/ai-learn` 
                            : `/courses/${courseId}/learn`}
                          className="btn-gold w-full flex items-center justify-center gap-2"
                        >
                          <Play className="h-5 w-5" />
                          Continue Learning
                        </Link>
                      )}
                    </div>
                  ) : (
                    <button
                      onClick={handleEnroll}
                      disabled={enrolling}
                      className="btn-gold w-full flex items-center justify-center gap-2 disabled:opacity-50"
                      data-testid="enroll-btn"
                    >
                      {enrolling ? (
                        'Processing...'
                      ) : course.price > 0 ? (
                        <>
                          <ShoppingCart className="h-5 w-5" />
                          Enroll Now
                        </>
                      ) : (
                        <>
                          <Play className="h-5 w-5" />
                          {isEnterprise ? 'Start Course' : 'Start Free Course'}
                        </>
                      )}
                    </button>
                  )}

                  <div className="mt-6 pt-6 border-t border-zinc-800 space-y-3">
                    <div className="flex items-center gap-3 text-sm text-zinc-400">
                      <CheckCircle className="h-4 w-4 text-navigate-gold" />
                      <span>Retake the course at any time</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-zinc-400">
                      <CheckCircle className="h-4 w-4 text-navigate-gold" />
                      <span>Earn XP and badges upon completion</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-zinc-400">
                      <CheckCircle className="h-4 w-4 text-navigate-gold" />
                      <span>Track your progress on the dashboard</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Enrollment Modal */}
      {showEnrollmentModal && course && (
        <EnrollmentModal
          course={course}
          onClose={() => setShowEnrollmentModal(false)}
          onSuccess={handleEnrollmentSuccess}
        />
      )}
    </Layout>
  );
}
